import React, { useEffect, useRef, useState } from "react";
import { usePolling } from "../lib/usePolling";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Header } from "../components/Header";
import { TeamBadge } from "../components/TeamBadge";
import { useFollows } from "../context/FollowsContext";
import { FollowBell } from "../components/FollowBell";
import { useTranslation } from "react-i18next";
import { CaretLeft, Bell, Clock, CheckCircle, ShareNetwork } from "@phosphor-icons/react";

const pc = (x) => Math.round((x || 0) * 100);

function StatusTag({ m }) {
  const { t: tr } = useTranslation();
  if (m.is_live)
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-perf-danger/15 border border-perf-danger/40 text-perf-danger text-[10px] font-bold">
        <span className="w-1.5 h-1.5 rounded-full bg-perf-danger animate-pulse" />
        {m.minute ? `${m.minute}'` : tr("followed.live")}
      </span>
    );
  if (m.is_finished)
    return <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px] font-bold">{tr("followed.finished")}</span>;
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/15 border border-gold/40 text-gold text-[10px] font-bold">
      <Clock size={11} weight="bold" /> {tr("followed.upcoming")}
    </span>
  );
}

function MatchRow({ m, onOpen }) {
  const { t: tr } = useTranslation();
  const pr = m.prediction;
  const hasScore = m.score && m.score.home != null;
  return (
    <button
      data-testid={`followed-match-${m.fixture_id}`}
      onClick={onOpen}
      className="w-full text-left hm-card p-4 transition-transform active:scale-[0.99]"
      style={m.card_adjusted ? { borderColor: "rgba(229,57,53,0.4)" } : undefined}
    >
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gold/80 truncate">{m.competition || "Football"}</span>
        <StatusTag m={m} />
      </div>

      <div className="flex items-center gap-3">
        <TeamBadge team={m.home} size={34} />
        <span className="flex-1 text-sm font-bold truncate">{m.home.name}</span>
        {hasScore && <span className="font-numeral font-black text-lg" style={{ color: m.is_live ? "#00E676" : "#fff" }}>{m.score.home}</span>}
      </div>
      <div className="flex items-center gap-3 mt-1.5">
        <TeamBadge team={m.away} size={34} />
        <span className="flex-1 text-sm font-bold truncate">{m.away.name}</span>
        {hasScore && <span className="font-numeral font-black text-lg" style={{ color: m.is_live ? "#00E676" : "#fff" }}>{m.score.away}</span>}
      </div>

      {pr ? (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] text-white/55">{m.is_live ? tr("followed.pronostic_live") : tr("followed.pronostic")}</span>
            {m.card_adjusted && (
              <span className="px-2 py-0.5 rounded-full bg-perf-danger/20 border border-perf-danger/40 text-perf-danger text-[9px] font-black" data-testid={`flip-badge-${m.fixture_id}`}>
                🟥 {tr("followed.card_adjusted")}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold" data-testid={`fav-${m.fixture_id}`}>{pr.favorite.label}</span>
            <span className="font-numeral font-black text-gold">{pr.favorite.pct}%</span>
          </div>
          <div className="flex gap-2 mt-2 text-[10px] text-white/50">
            <span>1 {pc(pr.home)}%</span>
            <span>N {pc(pr.draw)}%</span>
            <span>2 {pc(pr.away)}%</span>
            <span className="ml-auto">{tr("followed.likely_score")} {pr.likely_score}{pr.likely_score_prob != null ? ` (${pr.likely_score_prob}%)` : ""}</span>
          </div>
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-white/40">{tr("followed.pred_unavailable")}</div>
      )}
    </button>
  );
}

export default function FollowedMatches() {
  const { t: tr } = useTranslation();
  const navigate = useNavigate();
  const { counts: followCounts } = useFollows();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [shareWins, setShareWins] = useState([]);
  const [tennisFollows, setTennisFollows] = useState([]);
  const [basketFollows, setBasketFollows] = useState([]);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const verdicts = useRef({});

  // Recharge la liste enrichie quand un match est suivi/désuivi n'importe où
  // dans l'app (counts changent) + rafraîchit le live toutes les 30s.
  usePolling((signal) => {
    // Audit Q18 : en cas d'échec, on CONSERVE les dernières listes valides et on signale l'échec ;
    // l'actualisation suivante (30 s, ou retour du réseau) reprend automatiquement.
    const ok = () => !signal.aborted;
    let failed = false;
    const fail = () => { if (ok()) { failed = true; } };
    return Promise.all([
      api.get("/live/following/live", { signal }).then((r) => setData(r.data.matches)).catch(() => { fail(); if (ok()) setData((d) => d ?? []); }),
      api.get("/tennis/following", { signal }).then((r) => setTennisFollows(r.data.matches || [])).catch(fail),
      api.get("/basket/following", { signal }).then((r) => setBasketFollows(r.data.matches || [])).catch(fail),
    ]).then(() => { if (ok()) setRefreshFailed(failed); });
  }, 30000, [followCounts.foot, followCounts.tennis, followCounts.basket]);

  // Rappel de partage : uniquement pour les matchs suivis TERMINÉS dont le
  // pronostic HITMAX s'est révélé juste (source vérifiée côté backend).
  useEffect(() => {
    if (!data) { setShareWins([]); return; }
    const finished = data.filter((m) => m.is_finished);
    if (!finished.length) { setShareWins([]); return; }
    // Une seule demande groupée ; un verdict définitif (match terminé) n'est plus jamais redemandé.
    const known = verdicts.current;
    const missing = finished.map((m) => `fx_${m.fixture_id}`).filter((id) => !known[id]);
    const show = () => setShareWins(finished.map((m) => ({ m, res: known[`fx_${m.fixture_id}`] }))
      .filter((x) => x.res?.shareable).map((x) => ({ ...x.m, _res: x.res })));
    if (!missing.length) { show(); return; }
    let on = true;
    api.post("/matches/prediction-results", { match_ids: missing })
      .then((r) => {
        Object.entries(r.data?.results || {}).forEach(([id, res]) => { if (res?.finished) known[id] = res; });
      })
      .catch(() => {})
      .then(() => { if (on) show(); });
    return () => { on = false; };
  }, [data]);

  const counts = {
    all: data ? data.length : 0,
    live: data ? data.filter((m) => m.is_live).length : 0,
    upcoming: data ? data.filter((m) => !m.is_live && !m.is_finished).length : 0,
    finished: data ? data.filter((m) => m.is_finished).length : 0,
  };
  const tabs = [
    { key: "all", label: tr("followed.tab_all") },
    { key: "live", label: tr("followed.tab_live") },
    { key: "upcoming", label: tr("followed.tab_upcoming") },
    { key: "finished", label: tr("followed.tab_finished") },
  ];
  const shown = !data
    ? []
    : data.filter((m) =>
        filter === "all"
          ? true
          : filter === "live"
          ? m.is_live
          : filter === "finished"
          ? m.is_finished
          : !m.is_live && !m.is_finished
      );

  return (
    <>
      <Header showSearch={false} />
      <div className="px-5 pt-5">
        {refreshFailed && (
          <p role="status" className="text-xs text-amber-300/90 bg-amber-500/10 rounded-lg px-3 py-2 mb-3" data-testid="followed-refresh-error">
            {tr("followed.refresh_failed", "Actualisation impossible pour le moment : affichage des dernières données. Nouvel essai automatique.")}
          </p>
        )}
        <button data-testid="followed-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-white/50 mb-4">
          <CaretLeft size={16} weight="bold" /> {tr("followed.back")}
        </button>
        <div className="flex items-center gap-2">
          <Bell size={20} weight="fill" color="#00E676" />
          <h1 className="font-display text-2xl font-bold tracking-tight">{tr("followed.title")}</h1>
        </div>
        <p className="text-sm text-white/50 mt-1">{tr("followed.subtitle")}</p>

        {shareWins.length > 0 && (
          <div className="mt-5 space-y-2.5" data-testid="share-reminder">
            {shareWins.map((m) => (
              <div key={m.fixture_id}
                className="rounded-2xl p-3.5 flex items-center gap-3"
                style={{ background: "linear-gradient(160deg, rgba(0,230,118,0.14), rgba(18,18,18,1))", border: "1px solid rgba(0,230,118,0.45)" }}>
                <CheckCircle size={26} weight="fill" color="#00E676" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-perf-excellent leading-tight">{tr("followed.confirmed")}</p>
                  <p className="text-[11px] text-white/60 truncate">
                    {m.home.name} vs {m.away.name}{m._res.score ? ` · ${m._res.score}` : ""} — {tr("followed.share_success")}
                  </p>
                </div>
                <button data-testid={`share-reminder-btn-${m.fixture_id}`}
                  onClick={() => navigate(`/match/fx_${m.fixture_id}`)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-perf-excellent text-black text-xs font-bold">
                  <ShareNetwork size={15} weight="bold" /> {tr("followed.share")}
                </button>
              </div>
            ))}
          </div>
        )}

        {data && data.length > 0 && (
          <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar" data-testid="followed-filters">
            {tabs.map((t) => {
              const active = filter === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`filter-${t.key}`}
                  onClick={() => setFilter(t.key)}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${
                    active ? "bg-perf-excellent text-black" : "glass text-white/60 hover:text-white"
                  }`}
                >
                  {t.key === "live" && !active && counts.live > 0 && <span className="w-1.5 h-1.5 rounded-full bg-perf-danger animate-pulse" />}
                  {t.label}
                  <span className={active ? "text-black/60" : "text-white/40"}>{counts[t.key]}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-5 space-y-3" data-testid="followed-list">
          {!data ? (
            [1, 2, 3].map((i) => <div key={i} className="h-32 rounded-2xl skeleton" />)
          ) : data.length === 0 ? (
            <div className="hm-card p-8 text-center" data-testid="followed-empty">
              <Bell size={32} weight="regular" color="rgba(255,255,255,0.3)" className="mx-auto mb-3" />
              <p className="text-sm font-semibold text-white/80">{tr("followed.empty_title")}</p>
              <p className="text-xs text-white/50 mt-1.5 max-w-xs mx-auto">{tr("followed.empty_desc")}</p>
            </div>
          ) : shown.length === 0 ? (
            <div className="hm-card p-6 text-center" data-testid="followed-filter-empty">
              <p className="text-sm text-white/60">{tr("followed.filter_empty")}</p>
            </div>
          ) : (
            shown.map((m) => <MatchRow key={m.fixture_id} m={m} onOpen={() => navigate(`/live/${m.fixture_id}`)} />)
          )}
        </div>

        {(tennisFollows.length > 0 || basketFollows.length > 0) && (
          <div className="mt-8 space-y-6" data-testid="other-sports-follows">
            {[["tennis", tennisFollows, "Tennis"], ["basket", basketFollows, "Basket"]].map(([sport, list, label]) =>
              list.length > 0 ? (
                <div key={sport}>
                  <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white/50 mb-2">{label} {tr("followed.followed_word")}</h2>
                  <div className="space-y-2.5">
                    {list.map((m, i) => (
                      <div key={i} data-testid={`followed-${sport}-${i}`}
                        onClick={() => navigate(`/prediction/${sport}/${encodeURIComponent(m.home)}/${encodeURIComponent(m.away)}`)}
                        className="hm-card p-3.5 flex items-center gap-3 cursor-pointer">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate">{m.home} <span className="text-white/40">vs</span> {m.away}</p>
                          {m.tournament && <p className="text-[11px] text-white/45 truncate">{m.tournament}</p>}
                        </div>
                        {m.finished && <span className="text-[10px] font-bold text-white/40">{tr("followed.finished")}</span>}
                        <FollowBell sport={sport} match={{ home: m.home, away: m.away, tournament: m.tournament }} size={15} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null
            )}
          </div>
        )}
        <div className="h-8" />
      </div>
    </>
  );
}
